import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import AppContext from '../components/context/AppContext';

const MAX_RETRIES = 3;

const isDataValid = (data = null) => typeof data === 'object' && data !== null && !(data instanceof Error);

const usePrefetch = ({ key, resourceKey = null, resourceValue = null, prefetchFn = async () => { } }) => {
    const { appContext } = useContext(AppContext);
    const [error, setError] = useState(null);
    const initialData = useMemo(() => {
        if (resourceKey !== null
            && resourceValue !== null
            && appContext
            && appContext[key]
            && appContext[key][resourceKey] !== resourceValue)
            return null;
        return appContext ? appContext[key] ?? null : null;
    }, [resourceKey, resourceValue, appContext]);
    const [data, setData] = useState(initialData);
    const [isLoaded, setIsLoaded] = useState(
        initialData !== null
        && Object.entries(initialData).length > 0
    );
    const executePrefetchFn = useCallback(async () => {
        try {
            setIsLoaded(false);
            let retries = MAX_RETRIES;
            let data = null;
            while (!isDataValid(data)) {
                if (retries === 0) {
                    break;
                }
                data = await prefetchFn();
                retries--;
            }
            if (!isDataValid(data)) {
                // Log prefetch error messages
                if (data instanceof Error) {
                    console.log(data.message);
                }
                throw new Error('Failed to prefetch data after multiple retries.');
            }
            setData(data);
            setIsLoaded(true);
        } catch (e) {
            console.error('Failed to prefetch data:');
            console.error(e);
            setError(e);
            setIsLoaded(true);
        }
    }, [prefetchFn, isDataValid]);
    useEffect(() => {
        if (initialData !== null
            && Object.entries(initialData).length > 0) {
            setIsLoaded(true);
            setData(initialData);
        } else {
            executePrefetchFn();
        }
    }, [initialData, resourceValue]);
    return {
        isLoaded,
        data,
        error,
    };
};

export default usePrefetch;